import { Check, ChevronsUpDown } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { getCitiesForProvince } from "@/lib/city-data"
import {
  COUNTRIES,
  getCountryCode,
  getProvincesForCountry,
} from "@/lib/location-data"
import { cn } from "@/lib/utils"

interface LocationSelectProps {
  country: string
  region: string
  city?: string
  onCountryChange: (value: string) => void
  onRegionChange: (value: string) => void
  onCityChange?: (value: string) => void
  errors?: {
    country?: string
    region?: string
    city?: string
  }
  showCity?: boolean
}

export default function LocationSelect({
  country,
  region,
  city,
  onCountryChange,
  onRegionChange,
  onCityChange,
  errors,
  showCity = true,
}: LocationSelectProps) {
  const [countryOpen, setCountryOpen] = useState(false)
  const [regionOpen, setRegionOpen] = useState(false)
  const [cityOpen, setCityOpen] = useState(false)

  const provinces = getProvincesForCountry(country)
  const selectedCountry = COUNTRIES.find((c) => c.name === country)
  const countryCode = getCountryCode(country)
  const cities =
    countryCode && region ? getCitiesForProvince(countryCode, region) : []

  function handleCountrySelect(value: string) {
    onCountryChange(value)
    setCountryOpen(false)
  }

  function handleRegionSelect(value: string) {
    onRegionChange(value)
    setRegionOpen(false)
  }

  function handleCitySelect(value: string) {
    if (onCityChange) onCityChange(value)
    setCityOpen(false)
  }

  return (
    <>
      {/* Country */}
      <div className="space-y-1.5">
        <Label>País</Label>
        <Popover open={countryOpen} onOpenChange={setCountryOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={countryOpen}
              className="w-full justify-between font-normal"
            >
              {selectedCountry ? (
                <span>
                  {selectedCountry.flag} {selectedCountry.name}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  Seleccionar país...
                </span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command>
              <CommandInput placeholder="Buscar país..." />
              <CommandList>
                <CommandEmpty>No se encontró el país.</CommandEmpty>
                <CommandGroup>
                  {COUNTRIES.map((c) => (
                    <CommandItem
                      key={c.code}
                      value={c.name}
                      onSelect={() => handleCountrySelect(c.name)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          country === c.name ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {c.flag} {c.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {errors?.country && (
          <p className="mt-1 text-sm text-red-500">{errors.country}</p>
        )}
      </div>

      {/* Province/Region */}
      <div className="space-y-1.5">
        <Label>Provincia / Región</Label>
        {provinces.length > 0 ? (
          <Popover open={regionOpen} onOpenChange={setRegionOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={regionOpen}
                className="w-full justify-between font-normal"
              >
                {region || (
                  <span className="text-muted-foreground">
                    Seleccionar provincia...
                  </span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
            >
              <Command>
                <CommandInput placeholder="Buscar provincia..." />
                <CommandList>
                  <CommandEmpty>No se encontró la provincia.</CommandEmpty>
                  <CommandGroup>
                    {provinces.map((p) => (
                      <CommandItem
                        key={p}
                        value={p}
                        onSelect={() => handleRegionSelect(p)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            region === p ? "opacity-100" : "opacity-0",
                          )}
                        />
                        {p}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : (
          <Input
            value={region}
            onChange={(e) => onRegionChange(e.target.value)}
            placeholder="Ej: Santa Fe"
          />
        )}
        {errors?.region && (
          <p className="mt-1 text-sm text-red-500">{errors.region}</p>
        )}
      </div>

      {/* City */}
      {showCity && onCityChange && (
        <div className="space-y-1.5">
          <Label>Ciudad</Label>
          {cities.length > 0 ? (
            <Popover open={cityOpen} onOpenChange={setCityOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={cityOpen}
                  className="w-full justify-between font-normal"
                >
                  {city && city.length > 0 ? (
                    city
                  ) : (
                    <span className="text-muted-foreground">
                      Buscar ciudad...
                    </span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
              >
                <Command>
                  <CommandInput
                    placeholder="Buscar ciudad..."
                    onValueChange={(search) => {
                      // Allow free-text entry: update city value as user types
                      if (
                        search &&
                        !cities.some(
                          (c) => c.toLowerCase() === search.toLowerCase(),
                        )
                      ) {
                        onCityChange(search)
                      }
                    }}
                  />
                  <CommandList>
                    <CommandEmpty>
                      <span className="text-muted-foreground text-sm">
                        Escribí el nombre de tu ciudad
                      </span>
                    </CommandEmpty>
                    <CommandGroup>
                      {cities.map((c) => (
                        <CommandItem
                          key={c}
                          value={c}
                          onSelect={() => handleCitySelect(c)}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              city === c ? "opacity-100" : "opacity-0",
                            )}
                          />
                          {c}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          ) : (
            <Input
              value={city ?? ""}
              onChange={(e) => onCityChange(e.target.value)}
              placeholder="Ej: Rosario"
            />
          )}
          {errors?.city && (
            <p className="mt-1 text-sm text-red-500">{errors.city}</p>
          )}
        </div>
      )}
    </>
  )
}
